import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ResourceAvailabilityStore } from "@app/services/store/resource-availability-store";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { fromLonLat } from "ol/proj";
import GeoJSON from "ol/format/GeoJSON";
import { RouteService } from "@app/services/http/route.service";
import { TicketStreamingStore } from "@app/services/streaming/ticket-streaming-store";
import { TicketActionDialogComponent } from "@app/view/planning/ticket-action-dialog/ticket-action-dialog.component";
import {
  ClientConfigService,
  TicketColoursConfig
} from "@app/core/config/client-config.service";
import { ScheduleProjectService } from "@app/services/project/schedule-project.service";
import { LocationModel } from "@app/models/rest/location";
import { LiveMapManager } from "@app/view/planning/live-map/live-map-manager";
import { ResourceAvailability } from "@app/models/rest/resource-availability";
import { MatCheckbox, MatCheckboxChange } from "@angular/material/checkbox";
import { MatSelect, MatSelectChange } from "@angular/material/select";
import { MatToolbar } from "@angular/material/toolbar";
import { MatOption } from "@angular/material/core";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";

@Component({
  selector: "app-live-map",
  templateUrl: "./live-map.component.html",
  styleUrls: ["./live-map.component.scss"],
  imports: [
    MatToolbar,
    MatCheckbox,
    MatSelect,
    MatOption,
    MatIconButton,
    MatIcon,
    MatTooltip
  ]
})
export class LiveMapComponent implements OnInit, OnChanges, AfterViewInit {
  private el = inject(ElementRef);
  ticketStore = inject(TicketStreamingStore);
  scheduleProjectService = inject(ScheduleProjectService);
  resourceAvailabilityStore = inject(ResourceAvailabilityStore);
  dialog = inject(MatDialog);
  routeService = inject(RouteService);
  private clientConfigService = inject(ClientConfigService);

  @Input() public dateFrom: Date = null;
  @Input() public dateTo: Date = null;
  @Output() ticketHoverChange = new EventEmitter<number[]>();
  mapManager: LiveMapManager = new LiveMapManager();
  public loading = true;
  public pending = false;
  public width = 0;
  public bingStyles = [
    { value: "CanvasLight", display: "Roads" },
    { value: "CanvasDark", display: "Roads (dark)" },
    { value: "Aerial", display: "Aerial" },
    { value: "AerialWithLabelsOnDemand", display: "Aerial (labelled)" },
    { value: "OrdnanceSurvey", display: "O/S" }
  ];
  bingMode: boolean = false;
  imageryStyle: string;
  private _colours: TicketColoursConfig;

  constructor() {
    const config = this.clientConfigService.config;
    this._colours = config.ticketColours;
    this.mapManager.ticketHoverChange.subscribe((event) => {
      this.ticketHoverChange.emit(event);
    });
    this.bingMode = config.maps.bingKey && config.maps.bingKey.length > 0;
    this.imageryStyle = config.maps.imagerySet;
  }

  ngOnInit() {
    // console.log('map config', this.clientConfigService.getMapConfig());
    this.width = this.el.nativeElement.offsetWidth;
    const config = this.clientConfigService.config;
    this.mapManager.initMap(
      config.maps.tileUrl,
      this.handleMapClick.bind(this),
      config.maps.bingKey,
      config.maps.imagerySet
    );
    this.scheduleProjectService.projectData
      .asObservable()
      .subscribe((project) => {
        // console.log('map received project data');
        project.eventStore.on("change", this.handleEventStoreChange.bind(this));
        project.resourceTimeRangeStore.on(
          "change",
          this.handleResourceTimeRangeStoreChange.bind(this)
        );
        project.resourceStore.on(
          "change",
          this.handleResourceStoreChange.bind(this)
        );

        (project.eventStore as any).forEachScheduledEvent(
          this.processEventStoreRecord,
          this
        );

        let features = project.resourceTimeRangeStore.records.map((x) =>
          this.resourceTimeRangeToFeature(x)
        );
        this.mapManager.availabilitySource.addFeatures(features);
        this.mapManager.availabilitySource.changed();
        // console.log('adding features', features);
        this.mapManager.fitMap();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // console.log('changes detected in live map');
    this.scheduleProjectService.projectData
      .getValue()
      .eventStore.forEachScheduledEvent(
        this.processEventStoreRecord as any,
        this
      );
    this.mapManager.filterEvents(this.dateFrom, this.dateTo);
    if (this.mapManager.jobSource) {
      this.mapManager.jobSource.forEachFeature((p1) => {
        if (p1.get("visible")) {
          this.addRouteShowRequest(p1);
        }
      });
    }
  }

  ngAfterViewInit(): void {
    setTimeout(
      function () {
        this.scheduleProjectService.projectData
          .getValue()
          .eventStore.forEachScheduledEvent(
            this.processEventStoreRecord as any,
            this
          );
        this.mapManager.filterEvents(this.dateFrom, this.dateTo);
        this.mapManager.calculateZoomToFit();
      }.bind(this),
      3000
    );
  }

  updateMapSize() {
    this.mapManager.map.updateSize();
  }

  resourceTimeRangeToFeature(record): Feature<any> {
    let entity: ResourceAvailability = record.get("entity");
    let location: LocationModel = null;
    const resource = this.scheduleProjectService.projectData
      .getValue()
      .resourceStore.getById(record.get("resourceId"));
    if (entity) {
      if (!entity.location) {
        location = entity.resource.location
          ? entity.resource.location
          : resource.get("entity").location;
      }
    }
    if (location) {
      const resourceName =
        resource && resource.get("entity")
          ? resource.get("entity").name
          : "Unknown";
      let homeIcon = new Feature({
        type: "home",
        id: record.get("id"),
        visible: this.mapManager.calculateFeatureVisibility(
          record.get("resourceId"),
          record.get("startDate"),
          record.get("endDate"),
          this.dateFrom,
          this.dateTo
        ),
        geometry: new Point(
          fromLonLat([location.longitude, location.latitude])
        ),
        startDate: record.get("startDate"),
        endDate: record.get("endDate"),
        resourceId: record.get("resourceId"),
        resourceName: resourceName
      });
      homeIcon.setId(record.get("id"));
      // console.log('adding homeicon', homeIcon);
      return homeIcon;
    }
  }

  processEventStoreRecord(record) {
    // console.log('processing event store record', record);
    const resource = this.scheduleProjectService.projectData
      .getValue()
      .resourceStore.getById(record.get("resourceId"));
    // console.log('feature resource', resource);
    const resourceName = resource
      ? resource.get("entity")
        ? resource.get("entity").name
        : "Unknown"
      : "Unknown";

    let ticketIcon = new Feature({
      type: "job",
      id: record.get("id"),
      geometry: new Point([record.get("posX"), record.get("posY")]),
      visible: this.mapManager.calculateFeatureVisibility(
        record.get("resourceId"),
        record.get("startDate"),
        record.get("endDate"),
        this.dateFrom,
        this.dateTo
      ),
      externalJobReference: record.get("externalJobRef"),
      startDate: record.get("startDate"),
      endDate: record.get("endDate"),
      arrivedTime: record.get("arrivedTime"),
      nextTicketId: record.get("nextTicketId"),
      fillColor: this._colours.status[record.get("ticketStatus")].background,
      borderColor: record.get("priorityBackground"),
      resourceId: record.get("resourceId"),
      resourceName: resourceName
    });
    ticketIcon.setId(record.get("id"));
    let existing = this.mapManager.jobSource.getFeatureById(record.get("id"));

    if (existing) {
      // console.log('updating existing feature');
      if (existing.get("nextTicketId") !== record.get("nextTicketId")) {
        existing.setProperties(ticketIcon.getProperties());
        this.addRouteShowRequest(existing);
      } else {
        existing.setProperties(ticketIcon.getProperties());
      }
      return;
    }
    this.mapManager.jobSource.addFeature(ticketIcon);
    if (ticketIcon.get("visible")) {
      this.addRouteShowRequest(ticketIcon);
    }
  }

  addRouteShowRequest(feature: Feature<any>) {
    let routeId = feature.get("type") + feature.get("id");
    // console.log('adding route loading request', routeId);
    let existingRoute = this.mapManager.routeSource.getFeatureById(routeId);
    if (existingRoute) {
      this.mapManager.routeSource.removeFeature(existingRoute);
    }
    this.routeService
      .getNextTicketRoute(
        feature.get("type") === "job" ? feature.get("id") : null,
        feature.get("type") === "home" ? feature.get("id") : null
      )
      .then((value) => {
        // console.log('got route', value);
        let routeFeature = new Feature({
          type: "route",
          id: routeId,
          ticketIdFrom: feature.get("id"),
          geometry: new GeoJSON().readGeometry(value.geometry, {
            featureProjection: "EPSG:3857",
            dataProjection: "EPSG:4326"
          }),
          startDate: feature.get("startDate"),
          endDate: feature.get("endDate"),
          resourceId: feature.get("resourceId"),
          visible: this.mapManager.calculateFeatureVisibility(
            feature.get("resourceId"),
            feature.get("startDate"),
            feature.get("endDate"),
            this.dateFrom,
            this.dateTo
          )
        });
        routeFeature.setId(routeId);
        this.mapManager.routeSource.addFeature(routeFeature);
      });
  }

  handleEventStoreChange($event) {
    // console.log('event store changed', $event);
    if ($event.action === "add" || $event.action === "update") {
      if ($event.records) {
        for (let i = 0; i < $event.records.length; i++) {
          let record = $event.records[i];
          this.processEventStoreRecord(record);
        }
      }
    }
    this.mapManager.map.render();
  }

  handleResourceTimeRangeStoreChange($event) {
    if ($event.action === "add") {
      let records = $event.records;
      let features = records.map((x) => this.resourceTimeRangeToFeature(x));
      this.mapManager.availabilitySource.addFeatures(features);
      this.mapManager.availabilitySource.changed();
      // console.log('adding features', features);
      this.mapManager.calculateZoomToFit();
    }
  }

  handleResourceStoreChange($event) {
    // console.log('resource store changed', $event);
    if ($event.action === "filter") {
      // console.log('applying filter', $event.records);
      this.mapManager.applyFilter($event.records, this.dateFrom, this.dateTo);
      this.mapManager.fitMap();
    }
  }

  toggleViewOption(option: string, $event: MatCheckboxChange) {
    let layer = null;
    switch (option) {
      case "tickets":
        layer = this.mapManager.jobLayer;
        break;
      case "routes":
        layer = this.mapManager.routeLayer;
        break;
      case "shifts":
        layer = this.mapManager.availabilityLayer;
        break;
    }
    if (layer) {
      layer.setVisible($event.checked);
    }
  }

  updateMapStyle($event: MatSelectChange) {
    this.mapManager.changeRender(
      this.clientConfigService.config.maps.bingKey,
      $event.value
    );
  }

  private handleMapClick(e) {
    console.log("map click", e);
    // this.highlightSource.clear();
    let features = this.mapManager.map.getFeaturesAtPixel(e.pixel);
    let finalFeatureList = [];
    for (let i = 0; i < features.length; i++) {
      if (features[i].get("type") === "job") {
        if (finalFeatureList.indexOf(features[i].get("id")) === -1) {
          finalFeatureList.push(features[i].get("id"));
        }
      }
    }
    console.log(finalFeatureList);
    finalFeatureList.forEach((value) => {
      this.openTicketDialog(value);
    });
  }

  private openTicketDialog(id) {
    this.dialog.open(TicketActionDialogComponent, {
      hasBackdrop: false,
      autoFocus: false,
      width: "500px",
      // position: { top: targetY + 'px', left: targetX + 'px' },
      panelClass: "toolbar-popup-dialog",
      data: { ticketId: id }
    });
  }
}
